import { FC, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAtomValue } from "jotai";
import { Avatar, Button, Chip } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { purple, pink, indigo, blue, teal, lime, yellow, amber, brown } from "@mui/material/colors";
import { ortexColorState, positiveState, negativeState, neutralState } from "@/lib/store";
import Module from "@/components/module/Module";
import EventTranscripts from "@/components/events-transcripts/EventsTranscripts";
import { RowDetail } from "@/models/stock-events";
import { askTranscript, getEventTranscript, getTranscriptAISummary } from "@/controllers/eventPage";
import { EventListRow, TranscriptAISummary, TranscriptDetail, TranscriptParticipant } from "@/models/event-page";
import { toneColor } from "./helpers";
import Lockable from "@/components/lockable/Lockable";
import styles from "./EventPageRevamp.module.scss";
import { UPGRADE_LOCATIONS } from "@/lib/tracking";
import useUpgradeCta from "src/hooks/useUpgradeCta";

// Same avatar palette / ordering used by the existing EventsTranscripts dialog,
// so the speaker preview matches the full transcript view.
const AVATAR_COLORS = [purple[500], pink[500], indigo[500], blue[500], teal[500], lime[500], yellow[500], amber[500], brown[500]];

const SUGGESTED_QUESTIONS = ["What did management say about margins?", "Summarise the guidance", "Any mention of buybacks?", "How are revenue trends?"];

interface ThreadEntry {
    id: number;
    question: string;
    loading: boolean;
    answer?: string;
    cites?: number[];
    error?: boolean;
}

interface TranscriptHubProps {
    event: EventListRow;
    /**
     * When true the viewer is below the Advanced tier (FREE or BASIC): the
     * transcript TEXT is still shown (Basic+ can read it), but the AI parts — the
     * "AI Summary of the Call" card and the Q&A box — are gated behind the upgrade
     * overlay. We skip the (billable) AI-summary fetch and short-circuit the Q&A
     * endpoint in that case. The parent only renders this hub for Basic+ viewers;
     * free users get the whole hub blurred by an outer <Lockable>.
     */
    lockedAI?: boolean;
}

/**
 * Earnings transcript hub for the events detail view: the AI summary of the call,
 * a speaker / excerpt preview, the gated "Ask this call" Q&A box, and the
 * full-transcript reader. The reader reuses the existing app-wide
 * <EventsTranscripts> MUI Dialog (fullscreen on mobile) rather than a bespoke
 * modal. Each async section degrades gracefully (loading / empty / error /
 * 403-upsell).
 */
const TranscriptHub: FC<TranscriptHubProps> = ({ event, lockedAI = false }) => {
    const accent = useAtomValue(ortexColorState);
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const neutral = useAtomValue(neutralState);

    const [transcript, setTranscript] = useState<TranscriptDetail | null>(null);
    const [transcriptLoading, setTranscriptLoading] = useState(true);
    const [transcriptError, setTranscriptError] = useState(false);

    const [summary, setSummary] = useState<TranscriptAISummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);

    const [thread, setThread] = useState<ThreadEntry[]>([]);
    const [question, setQuestion] = useState("");
    const [askGated, setAskGated] = useState(false);
    const unlockAsk = useUpgradeCta(UPGRADE_LOCATIONS.TRANSCRIPT_ASK, { event_id: event.id });

    const [modalOpen, setModalOpen] = useState(false);

    // Colour map per participant id (stable across renders).
    const participantColor = useMemo(() => {
        const map: Record<number, string> = {};
        (transcript?.participants ?? []).forEach((p, i) => {
            map[p.id] = AVATAR_COLORS[i % AVATAR_COLORS.length];
        });
        return map;
    }, [transcript]);

    // Fetch transcript text (always — Basic+ can read it) plus the AI call summary
    // (Advanced only — skipped when lockedAI).
    useEffect(() => {
        const controller = new AbortController();
        setTranscript(null);
        setTranscriptLoading(true);
        setTranscriptError(false);
        setSummary(null);
        setThread([]);
        setAskGated(false);

        // Transcript TEXT is a Basic+ feature, so always fetch and render it.
        getEventTranscript(event.id, event.externalId, controller.signal)
            .then((data) => {
                setTranscript(data);
                setTranscriptError(false);
            })
            .catch((err) => {
                if (!axios.isCancel(err)) setTranscriptError(true);
            })
            .finally(() => setTranscriptLoading(false));

        // The AI call summary is Advanced+. For below-Advanced viewers (lockedAI) the
        // parent blurs the summary card behind the upgrade overlay, so skip the
        // (billable) request and settle into a quiescent, non-loading state.
        if (lockedAI) {
            setSummary(null);
            setSummaryLoading(false);
        } else {
            setSummaryLoading(true);
            getTranscriptAISummary(event.id, event.externalId, controller.signal)
                .then((data) => setSummary(data))
                .catch(() => setSummary(null))
                .finally(() => setSummaryLoading(false));
        }

        return () => controller.abort();
    }, [event.id, lockedAI]);

    const inFlight = thread.some((e) => e.loading);

    const submitQuestion = (text?: string) => {
        // Q&A is an Advanced+ feature; never fire the billable ask endpoint for a
        // below-Advanced viewer (lockedAI) — the upgrade overlay is shown instead.
        if (lockedAI) return;
        const q = (text ?? question).trim();
        if (!q || askGated) return;
        const id = Date.now();
        setThread((prev) => [{ id, question: q, loading: true }, ...prev].slice(0, 4));
        setQuestion("");

        askTranscript(event.id, q, event.externalId)
            .then((res) => {
                setThread((prev) => prev.map((e) => (e.id === id ? { ...e, loading: false, answer: res?.answer ?? "", cites: res?.cites ?? [] } : e)));
            })
            .catch((err) => {
                if (axios.isAxiosError(err) && err.response?.status === 403) {
                    setAskGated(true);
                    setThread((prev) => prev.filter((e) => e.id !== id));
                } else {
                    setThread((prev) => prev.map((e) => (e.id === id ? { ...e, loading: false, error: true } : e)));
                }
            });
    };

    const speakers = transcript?.participants ?? [];
    const components = transcript?.transcript ?? [];
    const teaser = components.find((c) => c?.content)?.content ?? "";

    const summaryBullets = (summary?.bullets ?? []).filter((b) => b && b.text);
    const summaryTopics = (summary?.topics ?? []).filter(Boolean);
    const summaryHasContent = Boolean(summary && (summary.sentiment || summary.tldr || summaryBullets.length > 0 || summaryTopics.length > 0));

    // RowDetail handed to the existing EventsTranscripts dialog. It re-fetches the
    // same /get_transcript endpoint by id and renders the full reader.
    const transcriptRow: RowDetail = {
        id: event.id,
        event_id: event.id,
        external_id: event.externalId,
        headline: event.title,
        title: event.title,
        creation_date: event.date,
        mostImportantDatetime: event.date,
        transcript_id: 0,
    };

    const askPane = (
        <div className={styles.askBar}>
            {askGated ? (
                <div className={styles.upsell}>
                    <b>Ask this call is a premium feature.</b>
                    <span>Upgrade your plan to ask questions and get cited answers from call &amp; presentation transcripts.</span>
                    <Button variant="contained" size="small" disabled={unlockAsk.loading} onClick={unlockAsk.onClick} sx={{ alignSelf: "flex-start", color: "white", textTransform: "none" }}>
                        {unlockAsk.label}
                    </Button>
                </div>
            ) : (
                <>
                    <form
                        className={styles.askInput}
                        onSubmit={(e) => {
                            e.preventDefault();
                            submitQuestion();
                        }}
                    >
                        <input
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    submitQuestion();
                                }
                            }}
                            placeholder="Ask anything about this transcript…"
                            aria-label="Ask a question about this transcript"
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disableElevation
                            onClick={() => submitQuestion()}
                            disabled={!question.trim() || inFlight}
                            className={styles.askButton}
                            sx={{ textTransform: "none", backgroundColor: accent, "&:hover": { backgroundColor: accent, filter: "brightness(0.92)" } }}
                        >
                            Ask
                        </Button>
                    </form>
                    <div className={styles.askChips}>
                        {SUGGESTED_QUESTIONS.map((s) => (
                            <Chip key={s} label={s} size="small" variant="outlined" onClick={() => submitQuestion(s)} clickable />
                        ))}
                    </div>
                    {thread.map((entry) => (
                        <div key={entry.id} className={styles.answer}>
                            <div className={styles.answerQ}>
                                <Chip label="Q" size="small" sx={{ backgroundColor: accent, color: "#fff", height: 18, width: 18, "& .MuiChip-label": { px: 0 } }} />
                                {entry.question}
                            </div>
                            {entry.loading ? (
                                <div className={styles.answerThinking}>reading transcript…</div>
                            ) : entry.error ? (
                                <div className={styles.answerThinking}>Couldn&apos;t answer that right now. Please try again.</div>
                            ) : (
                                <>
                                    <div className={styles.answerBody} dangerouslySetInnerHTML={{ __html: entry.answer ?? "" }} />
                                    {entry.cites && entry.cites.length > 0 && (
                                        <div className={styles.answerCites}>
                                            <span>Jump to quote</span>
                                            {entry.cites.map((c) => (
                                                <Chip key={c} label={`¶ ${c + 1}`} size="small" variant="outlined" clickable onClick={() => setModalOpen(true)} sx={{ color: accent, borderColor: accent }} />
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ))}
                </>
            )}
        </div>
    );

    // AI summary card. Advanced+ only — for below-Advanced viewers (lockedAI) it is
    // blurred behind the upgrade overlay (no data was fetched). Otherwise it shows the
    // generated summary (or a graceful empty/loading state).
    const aiSummaryNode = lockedAI ? (
        <Lockable locked minHeight={160}>
            <Module title="AI Summary of the Call">
                <div className={styles.aiCard}>
                    <p className={styles.aiHeadline}>&nbsp;</p>
                    <ul className={styles.aiBullets}>
                        <li>&nbsp;</li>
                        <li>&nbsp;</li>
                        <li>&nbsp;</li>
                    </ul>
                </div>
            </Module>
        </Lockable>
    ) : summaryLoading ? (
        <Module title="AI Summary of the Call">
            <div className={styles.loading}>Generating call summary…</div>
        </Module>
    ) : (
        <Module
            title="AI Summary of the Call"
            headerButton={{ content: <Chip label="BETA" size="small" variant="outlined" sx={{ color: accent, borderColor: accent, height: 18, fontSize: 9, fontWeight: 700, pointerEvents: "none" }} />, onClick: () => {} }}
        >
            <div className={styles.aiCard}>
                {summaryHasContent ? (
                    <>
                        <div className={styles.aiHeader}>
                            {summary?.sentiment && (
                                <Chip
                                    label={summary.sentiment}
                                    size="small"
                                    sx={{
                                        color: toneColor(summary.sentiment_tone ?? "neutral", positive, negative, neutral),
                                        borderColor: toneColor(summary.sentiment_tone ?? "neutral", positive, negative, neutral),
                                    }}
                                    variant="outlined"
                                />
                            )}
                            {summary?.read_mins ? <span className={styles.aiConfidence}>~{summary.read_mins} min read</span> : null}
                        </div>
                        {summary?.tldr && <p className={styles.aiHeadline}>{summary.tldr}</p>}
                        {summaryBullets.length > 0 && (
                            <ul className={styles.aiBullets}>
                                {summaryBullets.map((b, i) => (
                                    <li key={i}>
                                        <span className={styles.aiDot} style={{ background: toneColor(b.tone ?? "neutral", positive, negative, neutral) }} />
                                        <span dangerouslySetInnerHTML={{ __html: b.text ?? "" }} />
                                    </li>
                                ))}
                            </ul>
                        )}
                        {summaryTopics.length > 0 && (
                            <div className={styles.aiTopics}>
                                {summaryTopics.map((t) => (
                                    <Chip key={t} label={t} size="small" variant="outlined" />
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <div className={styles.empty}>AI summary is not available for this call yet.</div>
                )}
                <div className={styles.aiFoot}>AI-generated from the call transcript · verify against the source before acting</div>
            </div>
        </Module>
    );

    // Q&A box — Advanced+ only. Below-Advanced viewers (lockedAI) see it blurred
    // behind the upgrade overlay; submitQuestion() also early-returns for them.
    const askNode = lockedAI ? (
        <Lockable locked minHeight={160}>
            {askPane}
        </Lockable>
    ) : (
        askPane
    );

    return (
        <section className={styles.txHub}>
            {/* Left column: transcript header, ask box, speaker preview */}
            <div className={styles.txTranscriptCol}>
                <div className={styles.txHeaderRow}>
                    <h2>{event.type || "Call"}</h2>
                    {/* The availability chip doubles as the transcript CTA — once the
                        transcript has loaded it becomes clickable and opens the full reader. */}
                    {!transcriptError &&
                        (() => {
                            const ready = !transcriptLoading && components.length > 0;
                            return (
                                <Chip
                                    label={ready ? "Open full transcript" : "Transcript available"}
                                    size="small"
                                    variant="outlined"
                                    icon={ready ? <ArticleOutlinedIcon /> : undefined}
                                    clickable={ready}
                                    onClick={ready ? () => setModalOpen(true) : undefined}
                                    sx={{
                                        ml: "auto",
                                        fontWeight: 600,
                                        color: accent,
                                        borderColor: accent,
                                        "& .MuiChip-icon": { color: accent },
                                        ...(ready && { "&:hover": { backgroundColor: `${accent}1f` } }),
                                    }}
                                />
                            );
                        })()}
                </div>

                {transcriptLoading ? (
                    <div className={styles.loading}>Loading transcript…</div>
                ) : transcriptError || components.length === 0 ? (
                    <div className={styles.empty}>Transcript unavailable for this event.</div>
                ) : (
                    <div className={styles.txScrollBody}>
                        <div className={styles.txSub}>
                            <span>{speakers.length} speakers</span>
                            <span>·</span>
                            <span>{components.length} segments</span>
                        </div>
                        <div className={styles.txHubBody}>
                            <div>{askNode}</div>
                            <aside className={styles.txPreview}>
                                <span className={styles.railLabel}>On the call</span>
                                <div className={styles.txSpeakers}>
                                    {speakers.slice(0, 6).map((p: TranscriptParticipant) => (
                                        <span className={styles.txSpeaker} key={p.id}>
                                            <Avatar sx={{ bgcolor: participantColor[p.id], width: 24, height: 24, fontSize: 10 }}>{p.initials ?? ""}</Avatar>
                                            <span>{p.name ?? ""}</span>
                                        </span>
                                    ))}
                                </div>
                                {teaser && (
                                    <div className={styles.txExcerpt}>
                                        <span className={styles.railLabel}>Opening remarks</span>
                                        <p dangerouslySetInnerHTML={{ __html: teaser }} />
                                    </div>
                                )}
                            </aside>
                        </div>
                    </div>
                )}
            </div>

            {/* Right column: AI summary of the call (gated on lockedAI) */}
            {aiSummaryNode}

            {/* Full transcript reader — the existing app-wide dialog. */}
            {modalOpen && <EventTranscripts open={modalOpen} setOpen={setModalOpen} row={transcriptRow} />}
        </section>
    );
};

export default TranscriptHub;
